import React from 'react';
import { motion } from 'framer-motion';
import { Search, Users, BarChart } from 'lucide-react';
import Container from '../layout/Container';
import Button from '../ui/Button';

const Hero: React.FC = () => {
  const heroImage = "https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=1800";

  return (
    <section className="relative overflow-hidden min-h-[600px] flex items-center justify-center">
      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/90 to-neutral-900/60" />
      </div>

      <Container className="relative z-10 flex flex-col items-center justify-center text-center pt-32 pb-20 md:pt-40 md:pb-28">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold font-serif mb-6 leading-tight text-white">
            Premium IDX Solutions for Real Estate Professionals
          </h1>
          <p className="text-xl text-neutral-200 mb-8 max-w-lg mx-auto">
            Elevate your online presence with high-end features, lead capture, and AI-driven tools designed for today's competitive market.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button variant="primary" size="lg" to="/features">
              Explore Features
            </Button>
            <Button variant="outline" size="lg" to="/pricing" className="bg-transparent border-white text-white hover:bg-white/10">
              View Pricing
            </Button>
          </div>
        </motion.div>

        {/* Feature Bullets */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 w-full max-w-4xl">
          <motion.div
            className="flex items-start space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <div className="bg-primary-500 rounded-full p-3 text-white">
              <Search size={24} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Advanced Tools</h3>
              <p className="text-neutral-300">Innovative features for real estate professionals.</p>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
          >
            <div className="bg-primary-500 rounded-full p-3 text-white">
              <Users size={24} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Lead Generation</h3>
              <p className="text-neutral-300">Smart lead capture and CRM integration.</p>
            </div>
          </motion.div>

          <motion.div
            className="flex items-start space-x-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="bg-primary-500 rounded-full p-3 text-white">
              <BarChart size={24} />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white mb-2">Market Insights</h3>
              <p className="text-neutral-300">Analytics and reports for informed decisions.</p>
            </div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
};

export default Hero;